import { useState, useEffect, useMemo } from 'react';
import { Card, Select, Button, Spin, Alert, Empty, Typography } from 'antd';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import type { DotItemDotProps } from 'recharts';
import { 
  sessionWeatherService, 
  type SessionWeatherData, 
  type StationWeatherData,
  getMetricValue as getMetricValueFromStation
} from '../../api/sessionWeatherService';
import { sessionsService, type Session } from '../../api/sessionsService';
import { useEvent } from '../../hooks/useEvent';
import { useObservationFields } from '../../hooks/useObservationFields';
import { getFieldIcon } from '../Forecast/icons/fieldIconMap';
import { parseDateOnly } from '../../utils/dateUtils';
import './HistoricalWeatherTrend.css';

const { Text } = Typography;

interface HistoricalWeatherTrendProps {
  circuitId: string;
  /** Overrides selected event when viewing analytics outside event context */
  demo?: boolean;
  legacy?: boolean;
  circuitTimeZone?: string;
}

// Conversion constant for wind speed (m/s to km/h)
const MS_TO_KMH = 3.6;

// Logical order for session names in the dropdown
const SESSION_ORDER = ['FP1', 'FP2', 'FP3', 'Q', 'SQ', 'SPRINT', 'RACE'];

// Fields that require m/s to km/h conversion
const WIND_SPEED_FIELDS = ['windSpeed', 'windGust'];

// Weather metric with resolved unit
interface WeatherMetric {
  label: string;
  unit: string;
  metricName: string;      // Observation field name
  valueType: 'avg' | 'min' | 'max' | 'sum' | 'count';
  convertToKmh?: boolean;
  isDirection?: boolean;   // If true, this is wind direction (not a numeric metric)
}

export const HistoricalWeatherTrend = ({
  circuitId,
  demo: demoProp,
  legacy: legacyProp,
  circuitTimeZone: circuitTimeZoneProp,
}: HistoricalWeatherTrendProps) => {
  const { selectedEvent } = useEvent();
  const effectiveDemo = demoProp ?? selectedEvent?.demo;
  const legacyForRefresh = legacyProp ?? selectedEvent?.legacy;
  const effectiveCircuitTimeZone = circuitTimeZoneProp ?? selectedEvent?.circuit?.time_zone;
  const { getSessionCompareFields, getDisplayUnit } = useObservationFields();
  const [selectedSession, setSelectedSession] = useState<string>('');
  const [selectedStation, setSelectedStation] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weatherData, setWeatherData] = useState<SessionWeatherData[]>([]);
  const [sessions, setSessions] = useState<Map<string, Session>>(new Map());
  const [availableStations, setAvailableStations] = useState<string[]>([]);
  const [circuitSessionNames, setCircuitSessionNames] = useState<string[]>([]);

  // Convert wind direction to arrow (same as SessionComparison)
  const getWindDirectionArrow = (direction: string | null | undefined): string => {
    if (!direction) {
      return 'N/A';
    }
    
    const directionMap: Record<string, string> = {
      'n': '↑',
      'nne': '↑↗',
      'ne': '↗',
      'ene': '→↗',
      'e': '→',
      'ese': '→↘',
      'se': '↘',
      'sse': '↓↘',
      's': '↓',
      'ssw': '↓↙',
      'sw': '↙',
      'so': '↙', // French: Sud-Ouest (Southwest)
      'wsw': '←↙',
      'w': '←',
      'wnw': '←↖',
      'nw': '↖',
      'no': '↖', // French: Nord-Ouest (Northwest)
      'nnw': '↑↖',
      'o': '→', // French: Ouest (West) / Spanish: Oeste (East)
      'oso': '→↘',
      'sso': '↓↘',
      'ono': '→↗',
      'nno': '↑↗',
    };
    
    return directionMap[direction.toLowerCase()] || direction.toUpperCase();
  };

  // Build session options from distinct session names for this circuit (across all years)
  const sessionOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { label: string; value: string }[] = [];
    for (const name of circuitSessionNames) {
      if (name && !seen.has(name)) {
        seen.add(name);
        options.push({
          label: sessionsService.getSessionDisplayName(name),
          value: name,
        });
      }
    }
    return options;
  }, [circuitSessionNames]);

  // Fetch distinct session names for the circuit (all years) when circuit changes
  useEffect(() => {
    const fetchCircuitSessions = async () => {
      if (!circuitId) {
        setCircuitSessionNames([]);
        return;
      }
      try {
        const allSessions = await sessionsService.getSessions({
          circuit_id: circuitId,
        });
        const uniqueNames = [...new Set(allSessions.map((s) => s.session_name).filter(Boolean))];
        const names = [...uniqueNames].sort((a, b) => {
          const i = SESSION_ORDER.indexOf(a);
          const j = SESSION_ORDER.indexOf(b);
          if (i >= 0 && j >= 0) return i - j;
          if (i >= 0) return -1;
          if (j >= 0) return 1;
          return a.localeCompare(b);
        });
        setCircuitSessionNames(names);
      } catch (err) {
        console.error('Failed to fetch circuit sessions:', err);
        setCircuitSessionNames([]);
      }
    };
    fetchCircuitSessions();
  }, [circuitId]);

  // Build weather metrics dynamically from observation_fields table
  // Fields with include_in_session_compare = true will appear here (aligned with SessionComparison)
  const weatherMetrics: WeatherMetric[] = useMemo(() => {
    const fields = getSessionCompareFields(effectiveDemo);
    
    return fields.map(field => {
      const isWindField = WIND_SPEED_FIELDS.includes(field.name);
      const isDirectionField = field.name.toLowerCase().includes('direction');
      
      // For wind fields, display in km/h even though stored in m/s
      const displayUnit = isWindField ? 'km/h' : (getDisplayUnit(field.name) || field.unit || '');
      
      // Determine value type: use 'sum' for rain/precipitation, 'avg' for others
      // Wind direction doesn't use valueType since it's not a numeric metric
      const valueType: 'avg' | 'sum' = field.name.toLowerCase().includes('rain') || 
                                        field.name.toLowerCase().includes('precip')
        ? 'sum' 
        : 'avg';
      
      return {
        label: isDirectionField ? field.display_name : `${field.display_name} ${valueType === 'sum' ? 'total' : 'avg'}`,
        unit: displayUnit,
        metricName: field.name,  // Use observation field name directly
        valueType,
        convertToKmh: isWindField,
        isDirection: isDirectionField,
      };
    });
  }, [getSessionCompareFields, getDisplayUnit, effectiveDemo]);

  // Reset selected session when event/session options change
  useEffect(() => {
    const validValues = sessionOptions.map(o => o.value);
    if (validValues.length === 0) {
      setSelectedSession('');
    } else if (!validValues.includes(selectedSession)) {
      setSelectedSession(validValues[0]);
    }
  }, [sessionOptions, selectedSession]);

  // Fetch historical data when session or circuit changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch weather data
        const response = await sessionWeatherService.getHistoricalWeatherByCircuitAndSession(
          circuitId,
          selectedSession,
          'legacy' // Use legacy data
        );
        
        // The service returns the SessionWeatherResponse object directly
        // which has the structure: { success: true, data: [...], count, source }
        // Check if response is the data array or has a data property
        const sessionData = Array.isArray(response) ? response : (response.data || []);
        
        // Sort by year ascending (oldest first)
        const sortedData = sessionData.sort((a, b) => a.season_year - b.season_year);
        setWeatherData(sortedData);
        
        // Fetch all sessions for this circuit and session name
        // Sessions are now embedded in events (no source parameter needed)
        const allSessions = await sessionsService.getSessions({
          circuit_id: circuitId,
          session_name: selectedSession,
        });
        
        // Create a map of sessions keyed by season_year-circuit_id-start_date-session_name
        const sessionsMap = new Map<string, Session>();
        allSessions.forEach(session => {
          const key = `${session.season_year}-${session.circuit_id}-${session.start_date}-${session.session_name}`;
          sessionsMap.set(key, session);
        });
        setSessions(sessionsMap);
        
        // Extract available stations from ALL sessions (not just the first one)
        const allStationRefs = new Set<string>();
        sessionData.forEach((session) => {
          if (session.stations && session.stations.length > 0) {
            session.stations.forEach((s: StationWeatherData) => {
              allStationRefs.add(s.station_reference);
            });
          }
        });
        
        const stationRefs = Array.from(allStationRefs);
        setAvailableStations(stationRefs);
        
        // Auto-select 'all' if available, otherwise first station
        if (stationRefs.includes('all')) {
          setSelectedStation('all');
        } else if (stationRefs.length > 0) {
          setSelectedStation(stationRefs[0]);
        }
      } catch (err) {
        console.error('Failed to fetch historical weather data:', err);
        setError('Failed to load historical weather data');
        setWeatherData([]);
        setAvailableStations([]);
      } finally {
        setLoading(false);
      }
    };

    if (circuitId && selectedSession) {
      fetchData();
    }
  }, [circuitId, selectedSession, legacyForRefresh]);

  const formatDateTime = (dateStr: string) => {
    const date = parseDateOnly(dateStr);
    const month = date.toLocaleDateString('en-US', { month: '2-digit' });
    const day = date.toLocaleDateString('en-US', { day: '2-digit' });
    return `${month}/${day}`;
  };

  // Format session start and end datetime in circuit timezone
  // Returns { date, time } for display - uses session times (not event times)
  const formatSessionStartEnd = (
    startTime: string,
    endTime: string,
    timeZone?: string
  ): { date: string; time: string } => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const opts: Intl.DateTimeFormatOptions = { timeZone: timeZone, month: '2-digit', day: '2-digit' };
    const timeOpts: Intl.DateTimeFormatOptions = { timeZone: timeZone, hour: '2-digit', minute: '2-digit', hour12: false };
    const startDate = start.toLocaleDateString('en-US', opts);
    const endDate = end.toLocaleDateString('en-US', opts);
    const startTimeStr = start.toLocaleTimeString('en-US', timeOpts);
    const endTimeStr = end.toLocaleTimeString('en-US', timeOpts);
    if (startDate === endDate) {
      return { date: startDate, time: `${startTimeStr}-${endTimeStr}` };
    }
    return { date: `${startDate} - ${endDate}`, time: `${startTimeStr} - ${endTimeStr}` };
  };

  // Get value for a specific station and metric using the new structure
  // Applies conversion from m/s to km/h if needed
  // Returns number for numeric metrics, or MainWindDirection for wind direction
  const getMetricValue = (session: SessionWeatherData, metric: WeatherMetric): number | null => {
    const station = session.stations.find(s => s.station_reference === selectedStation);
    if (!station) return null;
    
    // Special handling for wind direction
    if (metric.isDirection) {
      // Return null if no wind direction data (we'll handle this in renderMetricRow)
      return null;
    }
    
    const rawValue = getMetricValueFromStation(station, metric.metricName, metric.valueType);
    if (rawValue === null) return null;
    return metric.convertToKmh ? rawValue * MS_TO_KMH : rawValue;
  };

  // Get wind direction for a specific station
  const getWindDirection = (session: SessionWeatherData): { direction: string; frequency: number } | null => {
    const station = session.stations.find(s => s.station_reference === selectedStation);
    if (!station || !station.main_wind_direction) return null;
    return station.main_wind_direction;
  };

  // Custom year label component (no value, just year)
  const YearLabel = (props: DotItemDotProps) => {
    const { cx, cy } = props;
    if (cx === undefined || cy === undefined) return null;
    
    const { payload } = props;
    const year = payload?.year;
    const date = payload?.date;
    const time = payload?.time;
    if (year === undefined) return null;
    
    return (
      <g>
        {/* Year */}
        <text
          x={cx}
          y={(cy as number) - 10}
          textAnchor="middle"
          fill="var(--year-label-color, rgba(255, 255, 255, 0.85))"
          fontSize="16"
          fontWeight="600"
        >
          {year}
        </text>
        {/* Date */}
        {date && (
          <text
            x={cx}
            y={(cy as number) + 5}
            textAnchor="middle"
            fill="var(--date-label-color, rgba(255, 255, 255, 0.45))"
            fontSize="12"
          >
            {date}
          </text>
        )}
        {/* Time */}
        {time && (
          <text
            x={cx}
            y={(cy as number) + 20}
            textAnchor="middle"
            fill="var(--date-label-color, rgba(255, 255, 255, 0.45))"
            fontSize="11"
          >
            {time}
          </text>
        )}
      </g>
    );
  };

  // Custom dot component to show values on bottom
  const CustomDot = (props: DotItemDotProps) => {
    const { cx, cy } = props;
    if (cx === undefined || cy === undefined) return null;
    
    const { payload } = props;
    const value = payload?.value;
    if (value === undefined) return null;
    
    return (
      <g>
        {/* Dot circle */}
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill="#FFA500"
          stroke="var(--dot-stroke-color, #1a1a1a)"
          strokeWidth={2}
        />
        {/* Value label - moved below the dot */}
        <text
          x={cx}
          y={(cy as number) + 18}
          textAnchor="middle"
          fill="var(--value-text-color, #FFA500)"
          fontSize="12"
          fontWeight="600"
        >
          {Math.round(value)}
        </text>
      </g>
    );
  };

  // Render a metric row
  const renderMetricRow = (metric: WeatherMetric) => {
    // Special handling for wind direction - display as text values instead of chart
    if (metric.isDirection) {
      const directionData = weatherData.map((session) => {
        const windDir = getWindDirection(session);
        return {
          year: session.season_year,
          date: formatDateTime(session.start_date),
          direction: windDir,
        };
      });
      
      const hasData = directionData.some(d => d.direction !== null);
      
      if (!hasData) {
        return (
          <div key={metric.metricName} className="metric-row">
            <div className="metric-label">
              <span className="metric-icon">{getFieldIcon(metric.metricName, { size: 18 })}</span>
              <span>{metric.label}</span>
            </div>
            <div className="metric-data">
              <div className="no-data-message">No data</div>
            </div>
          </div>
        );
      }
      
      // Render wind direction as text values aligned with year positions
      return (
        <div key={metric.metricName} className="metric-row">
          <div className="metric-label">
            <span className="metric-icon">{getFieldIcon(metric.metricName, { size: 18 })}</span>
            <span>{metric.label}</span>
          </div>
          <div className="metric-data">
            <div className="recharts-container">
              <ResponsiveContainer width="100%" height={80}>
                <LineChart 
                  data={directionData.map((d, index) => ({
                    year: d.year,
                    date: d.date,
                    value: 1, // dummy value for positioning
                    index
                  }))} 
                  margin={{ top: 20, right: 60, left: 60, bottom: 30 }}
                >
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="transparent"
                    strokeWidth={0}
                    dot={(props: DotItemDotProps) => {
                      const { cx, cy } = props;
                      if (cx === undefined || cy === undefined) return <g key="empty" />;
                      
                      const { payload } = props;
                      const year = payload?.year;
                      const index = payload?.index;
                      if (year === undefined || index === undefined) return <g key={`empty-${index ?? 'unknown'}`} />;
                      
                      const windDir = directionData[index]?.direction;
                      if (!windDir) return <g key={`no-data-${index}`} />;
                      
                      return (
                        <g key={`wind-dir-${index}`}>
                          <text
                            x={cx}
                            y={(cy as number) - 8}
                            textAnchor="middle"
                            fill="var(--value-text-color, #FFA500)"
                            fontSize="16"
                            fontWeight="600"
                          >
                            {getWindDirectionArrow(windDir.direction)}
                          </text>
                          <text
                            x={cx}
                            y={(cy as number) + 8}
                            textAnchor="middle"
                            fill="var(--date-label-color, rgba(255, 255, 255, 0.65))"
                            fontSize="11"
                          >
                            {windDir.direction.toUpperCase()} ({windDir.frequency.toFixed(1)}%)
                          </text>
                        </g>
                      );
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      );
    }
    
    // Prepare data for Recharts (numeric metrics)
    const chartData = weatherData.map((session, index) => {
      const value = getMetricValue(session, metric);
      return {
        year: session.season_year,
        date: formatDateTime(session.start_date),
        value: value !== null ? value : undefined,
        index
      };
    });
    
    const hasData = chartData.some(d => d.value !== undefined);
    
    if (!hasData) {
      return (
        <div key={metric.metricName} className="metric-row">
          <div className="metric-label">
            <span className="metric-icon">{getFieldIcon(metric.metricName, { size: 18 })}</span>
            <span>{metric.label} ({metric.unit})</span>
          </div>
          <div className="metric-data">
            <div className="no-data-message">No data</div>
          </div>
        </div>
      );
    }
    
    return (
      <div key={metric.metricName} className="metric-row">
        <div className="metric-label">
          <span className="metric-icon">{getFieldIcon(metric.metricName, { size: 18 })}</span>
          <span>{metric.label} ({metric.unit})</span>
        </div>
        <div className="metric-data">
          <div className="recharts-container">
            <ResponsiveContainer width="100%" height={80}>
              <LineChart data={chartData} margin={{ top: 20, right: 60, left: 60, bottom: 30 }}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#FFA500"
                  strokeWidth={2}
                  dot={CustomDot}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="historical-weather-card">
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="historical-weather-card">
        <Alert type="error" title="Error" description={error} showIcon />
      </Card>
    );
  }

  return (
    <Card className="historical-weather-card" variant="borderless">
      <div className="historical-weather-header">
        <div className="session-selector">
          <Text strong style={{ marginRight: 8 }}>Session</Text>
          <Select
            value={selectedSession || undefined}
            onChange={setSelectedSession}
            options={sessionOptions}
            placeholder={sessionOptions.length === 0 ? 'No sessions' : undefined}
            disabled={sessionOptions.length === 0}
            style={{ width: 180 }}
          />
        </div>
        
        <div className="station-selector">
          <Text strong style={{ marginRight: 8 }}>Stations</Text>
          <div className="station-buttons">
            {availableStations
              .filter(ref => ref !== 'all')
              .sort((a, b) => {
                // Sort numerically if both are numbers
                const numA = parseInt(a, 10);
                const numB = parseInt(b, 10);
                if (!isNaN(numA) && !isNaN(numB)) {
                  return numA - numB;
                }
                return a.localeCompare(b);
              })
              .map(stationRef => (
                <Button
                  key={stationRef}
                  type={selectedStation === stationRef ? 'primary' : 'default'}
                  shape="circle"
                  onClick={() => setSelectedStation(stationRef)}
                  size="small"
                >
                  {stationRef}
                </Button>
              ))}
            {availableStations.includes('all') && (
              <Button
                type={selectedStation === 'all' ? 'primary' : 'default'}
                onClick={() => setSelectedStation('all')}
                size="small"
                style={{ borderRadius: '50%', minWidth: 32 }}
              >
                all
              </Button>
            )}
          </div>
        </div>
      </div>

      {weatherData.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <Empty description="No historical weather data available for this session" />
        </div>
      ) : (
      <>
      <div className="weather-timeline">
        {/* Year header chart row - invisible line, just year labels */}
        <div className="metric-row year-row">
          <div className="metric-label">
            {/* Empty spacer */}
          </div>
          <div className="metric-data">
            <div className="recharts-container">
              <ResponsiveContainer width="100%" height={70}>
                <LineChart 
                  data={weatherData.map((session, index) => {
                    // Get the matching session data for session start/end datetime (not event times)
                    const sessionKey = `${session.season_year}-${session.circuit_id}-${session.start_date}-${session.session_name}`;
                    const sessionInfo = sessions.get(sessionKey);
                    const circuitTimeZone = effectiveCircuitTimeZone;
                    
                    let dateStr = formatDateTime(session.start_date);
                    let timeStr = '00:00';
                    if (sessionInfo?.start_time && sessionInfo?.end_time) {
                      const { date, time } = formatSessionStartEnd(
                        sessionInfo.start_time,
                        sessionInfo.end_time,
                        circuitTimeZone
                      );
                      dateStr = date;
                      timeStr = time;
                    }
                    
                    return {
                      year: session.season_year,
                      date: dateStr,
                      time: timeStr,
                      value: 1, // dummy value
                      index
                    };
                  })} 
                  margin={{ top: 40, right: 60, left: 60, bottom: 10 }}
                >
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="transparent"
                    strokeWidth={0}
                    dot={YearLabel}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="metrics-container">
          {weatherMetrics.map((metric) => renderMetricRow(metric))}
        </div>
      </div>

      <div className="weather-note">
        <Text type="secondary" style={{ fontSize: 11 }}>
          * When all stations are selected, the value represents the average rainfall totals recorded by stations over the sessions.
        </Text>
      </div>
      </>
      )}
    </Card>
  );
};
